import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput, materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {color} from "@cm/graph-v3"
import {z} from "zod/v4-mini"
import {keepLegacyColorMapping, keepLegacyTextureMapping} from "#material/nodes/texture/legacy-texture-settings"
import {imageTextureImageColorspaceSettingsNameSchema} from "#material/nodes/texture/tex-image/types"
import type {
    ImageTextureExtension,
    ImageTextureImageColorspaceSettingsName,
    ImageTextureInterpolation,
    ImageTextureProjection,
} from "#material/nodes/texture/tex-image/types"

export interface TextureFromRevisionSockets extends MaterialNodeSockets {
    inputs: {
        vector: "vector"
    }
    settings: {
        alpha: "float" // found in inputs: -; found in settings: float; inferred from data: float
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        colorspace: "string"
        disableProgressiveLoading: "boolean"
        forceOriginalResolution: "boolean"
        imageFilepath: "string" // found in inputs: -; found in settings: string; inferred from data: string
        projectionBlend: "float" // found in inputs: -; found in settings: float; inferred from data: float
        textureRevisionLegacyId: "int"
        vector: "vector" // found in inputs: point,color; found in settings: vector,input; inferred from data: float3,null
    }
    settingsExtensions: {
        imageColorspaceSettingsName?: ImageTextureImageColorspaceSettingsName
        extension?: ImageTextureExtension // found in inputs: -; found in settings: string; inferred from data: string
        interpolation?: ImageTextureInterpolation // found in inputs: -; found in settings: string; inferred from data: string
        projection?: ImageTextureProjection // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        color: "color"
        alpha: "float"
    }
}

export const textureFromRevisionDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeTexImage,
    },
    nodeClass: "TextureFromRevision",
    hierarchical: true,
    ui: {
        label: "Image Texture",
        color: "#9e6034",
        items: [
            {name: "color", valueType: "color", label: "Color", output: true},
            {name: "alpha", valueType: "float", label: "Alpha", output: true},
            {custom: true, componentClass: "textureRevision"},
            {name: "vector", valueType: "vector", label: "Vector", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
            // TODO: add more settings
        ],
        description: "Legacy node being phased out. Does not correspond to blender's TexImage!",
        support: {
            cycles: true,
            three: {full: false, remark: "Some filters/extension modes unsupported"},
        },
        width: 175,
    },
    validation: {
        returns: z.strictObject({color: materialColorOutput, alpha: materialFloatOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({
                vector: optionalMaterialInput,
            }),
            settings: z.strictObject({
                alpha: z.optional(z.number()),
                color: z.optional(color),
                ...keepLegacyColorMapping,
                colorspace: z.optional(z.string()),
                disableProgressiveLoading: z.optional(z.boolean()),
                extension: z.optional(z.enum(["REPEAT", "EXTEND", "CLIP", "MIRROR"])),
                forceOriginalResolution: z.optional(z.boolean()),
                imageColorspaceSettingsName: z.optional(imageTextureImageColorspaceSettingsNameSchema),
                imageFilepath: z.optional(z.string()),
                interpolation: z.optional(z.enum(["Linear", "Cubic", "Closest", "Smart"])),
                projection: z.optional(z.enum(["FLAT", "BOX", "SPHERE", "TUBE"])),
                projectionBlend: z.optional(z.number()),
                ...keepLegacyTextureMapping,
                textureRevisionLegacyId: z.optional(z.number()),
                vector: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<TextureFromRevisionSockets>
